import { Formik } from "formik";
import { useEffect, useState } from "react";
import Spin from "../../../../components/Spin";
import { Label } from "../../../../components/ui/label";
import CustomLabelText from "../../../../components/CustomLabelText";
import { Input } from "../../../../components/ui/input";
import { API, fileUpload, get } from "../../../../config";
import SearchSelect from "../../../../components/SearchSelect";
import { Button } from "../../../../components/ui/button";
import { AddAdminSectionSchema } from "../../../../lib/Validation";
import { sectionTypeOptions } from "../../../../config";
import ColorPicker from "../../../../components/ColorPicker";
import { useAllOrganizationTypes } from "../../../Auth/SignUpHelper";
import { Loader2 } from "lucide-react";

const AddEditSection = ({
  id,
  toggleShowAddEditSection,
  edit,
  fetchSectionData,
}) => {
  const [sectionData, setSectionData] = useState([]);
  const [loading, setLoading] = useState(false);

  const [versionLoading, setVersionLoading] = useState(false);
  const [versionListing, setVersionListing] = useState([]);
  const [versionList, setVersionList] = useState([]);

  const { allOrganizationTypeLoading, allOrganizationType = [] } =
    useAllOrganizationTypes({ amlCTFType: true });
  const organizationType = allOrganizationType?.map((item) => {
    const { id: value, name, short_name, ...rest } = item;
    return { label: name, value, ...rest };
  });

  async function fetchData() {
    try {
      setLoading(true);
      const reponse = await get(`${API.ADMIN_SECTION_DETAILS}/` + id);
      setSectionData(reponse?.data?.data);
      setLoading(false);
    } catch (e) {
      setSectionData(null);
    }
  }

  useEffect(() => {
    if (edit) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleFormSubmit = async (value) => {
    const formData = new FormData();
    formData.append(
      "organization_type_id",
      value?.organization_type_id ? value?.organization_type_id : ""
    );
    formData.append("version_id", value?.version_id ? value?.version_id : "");

    formData.append("order", value?.order ? value?.order : "");
    formData.append("title", value?.title ? value?.title : "");
    formData.append("sub_title", value?.sub_title ? value?.sub_title : "");
    formData.append("yes_color", value?.yes_color ? value?.yes_color : "");
    formData.append("no_color", value?.no_color ? value?.no_color : "");

    formData.append(
      "section_type",
      value?.section_type ? value?.section_type : ""
    );

    if (edit) {
      formData.append("_method", "put");
    }
    try {
      setLoading(true);
      const { data } = await fileUpload(
        `${API.ADMIN_SECTION_ADD}${edit ? `/${id}` : ""}`,
        formData
      );
      setLoading(false);
      if (data) {
        toggleShowAddEditSection();
        fetchSectionData();
      }
      return data?.data;
    } catch (error) {
      setLoading(false);
    }
  };

  const AdminVersionList = async () => {
    try {
      setVersionLoading(true);

      const { data } = await get(`${API.GET_ADMIN_VERSIONS}?limit=-1&page=1`);

      setVersionLoading(false);

      setVersionList(() => [...data.data.data]);
    } catch (e) {
      setVersionList([]);
      setVersionLoading(false);
      return false;
    }
  };

  useEffect(() => {
    AdminVersionList();
  }, []);

  useEffect(() => {
    if (versionList) {
      let versionTempListing = versionList?.map((item) => ({
        ...item,
        value: item?.id,
        label: `${item?.version_number} - ${item?.name}`,
      }));
      setVersionListing(versionTempListing || []);
    } else {
      setVersionListing([]);
    }
  }, [versionList]);

  return (
    <Formik
      initialValues={{
        organization_type_id: sectionData?.organization_type_id,
        version_id: sectionData?.version_id,
        section_type: sectionData?.section_type,
        order: sectionData?.order,
        title: sectionData?.title,
        sub_title: sectionData?.sub_title,
        yes_color: sectionData?.yes_color ? sectionData?.yes_color : "#ff0000",
        no_color: sectionData?.no_color ? sectionData?.no_color : "#007718",
      }}
      enableReinitialize={true}
      onSubmit={handleFormSubmit}
      validateOnBlur={true}
      validateOnChange={true}
      validationSchema={AddAdminSectionSchema}
    >
      {({
        values,
        errors,
        handleChange,
        handleBlur,
        handleSubmit,
        setFieldValue,
        isValid,
      }) => (
        <>
          {loading && <Spin />}
          <form
            onSubmit={handleSubmit}
            noValidate
            className="entry-section md:m-0 m-5"
          >
            <div className="form-box overflow-y-auto overflow-x-hidden max-h-[80vh] p-4 border rounded-lg">
              <div className="grid gap-4">
                <div className="grid md:grid-cols-1 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="type">
                      <CustomLabelText
                        required={true}
                        labelText="Industry Type"
                        errorField={errors?.organization_type_id}
                        bottomAlign={true}
                      />
                    </Label>
                    <div className="relative">
                      <SearchSelect
                        frameworks={organizationType}
                        optionType={"Industry Type"}
                        handleSelect={(e) => {
                          setFieldValue("organization_type_id", e);
                        }}
                        handleBlur={handleBlur}
                        className={
                          errors?.organization_type_id &&
                          `focus-visible:ring-red-500 border-red-500`
                        }
                        initialValue={values?.organization_type_id}
                        disabled={false}
                      />
                      {allOrganizationTypeLoading && (
                        <span className="absolute top-2 right-2 animate-spin">
                          <Loader2 width={20} height={20} />
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="type">
                      <CustomLabelText
                        required={true}
                        labelText="Version Number"
                        errorField={errors?.version_id}
                      />
                    </Label>
                    <div className="relative">
                      <SearchSelect
                        frameworks={versionListing}
                        optionType={"Version Number"}
                        handleSelect={(e) => {
                          setFieldValue("version_id", e);
                        }}
                        handleBlur={handleBlur}
                        className={
                          errors?.version_id &&
                          `focus-visible:ring-red-500 border-red-500`
                        }
                        initialValue={values?.version_id}
                        disabled={false}
                      />
                      {versionLoading && (
                        <span className="absolute top-2 right-2 animate-spin">
                          <Loader2 width={20} height={20} />
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="role_id">
                      <CustomLabelText
                        required={true}
                        labelText="Section Type"
                        errorField={errors?.section_type}
                        bottomAlign={true}
                      />
                    </Label>

                    <div className="relative">
                      <SearchSelect
                        frameworks={sectionTypeOptions}
                        optionType={"Section Type"}
                        handleSelect={(e) => {
                          setFieldValue("section_type", e);
                        }}
                        handleBlur={handleBlur}
                        className={
                          errors?.section_type &&
                          `focus-visible:ring-red-500 border-red-500`
                        }
                        disabled={false}
                        initialValue={values?.section_type}
                      />
                    </div>
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="order">
                      <CustomLabelText
                        required={true}
                        labelText="Order"
                        errorField={errors?.order}
                        bottomAlign={true}
                      />
                    </Label>
                    <Input
                      id="order"
                      type="number"
                      name="order"
                      min={1} // 🚫 no 0 or negatives
                      step={1}
                      inputMode="numeric"
                      pattern="[0-9]*"
                      value={values?.order || ""}
                      onKeyDown={(e) => {
                        // block dot, minus, exponent
                        if ([".", "-", "e", "E"].includes(e.key)) {
                          e.preventDefault();
                        }
                      }}
                      onChange={(e) => {
                        const value = e.target.value;

                        // allow empty while typing
                        if (value === "") {
                          handleChange(e);
                          return;
                        }

                        // only whole numbers greater than 0
                        if (/^[1-9]\d*$/.test(value)) {
                          handleChange(e);
                        }
                      }}
                      onBlur={handleBlur}
                      autoComplete="off"
                      onKeyPress={(e) => {
                        e.which === 13 && e.preventDefault();
                      }}
                      className={
                        errors?.order &&
                        `focus-visible:ring-red-500 border-red-500`
                      }
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="title">
                      <CustomLabelText
                        required={true}
                        labelText="Title"
                        errorField={errors?.title}
                      />
                    </Label>
                    <Input
                      id="title"
                      type="text"
                      name="title"
                      value={values?.title ? values?.title : ""}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      autoComplete="off"
                      onKeyPress={(e) => {
                        e.which === 13 && e.preventDefault();
                      }}
                      className={
                        errors?.title &&
                        `focus-visible:ring-red-500 border-red-500`
                      }
                    />
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="sub_title">
                      <CustomLabelText
                        required={false}
                        labelText="Sub Title"
                        errorField={errors?.sub_title}
                      />
                    </Label>
                    <Input
                      id="sub_title"
                      type="text"
                      name="sub_title"
                      value={values?.sub_title ? values?.sub_title : ""}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      autoComplete="off"
                      onKeyPress={(e) => {
                        e.which === 13 && e.preventDefault();
                      }}
                      className={
                        errors?.sub_title &&
                        `focus-visible:ring-red-500 border-red-500`
                      }
                    />
                  </div>
                  <div className="grid grid-cols-2">
                    <div className="grid gap-2">
                      <Label htmlFor="yes_color">
                        <CustomLabelText
                          required={true}
                          labelText="Yes Color"
                          errorField={errors?.yes_color}
                          bottomAlign={true}
                        />
                      </Label>
                      <ColorPicker
                        value={values?.yes_color}
                        onChange={(e) => setFieldValue("yes_color", e)}
                      />
                    </div>

                    {/* Default Checkbox */}

                    <div className="grid gap-2">
                      <Label htmlFor="no_color">
                        <CustomLabelText
                          required={true}
                          labelText="No Color"
                          errorField={errors?.no_color}
                          bottomAlign={true}
                        />
                      </Label>
                      <ColorPicker
                        value={values?.no_color}
                        onChange={(e) => setFieldValue("no_color", e)}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer pt-4 border-t mt-4 flex items-center justify-end">
              <Button type="submit" disabled={!isValid}>
                Save
              </Button>
              <Button
                className="reset ml-3"
                data-dismiss="modal"
                aria-label="Close"
                onClick={(e) => {
                  e.preventDefault();
                  toggleShowAddEditSection();
                }}
                variant="secondary"
              >
                Cancel
              </Button>
            </div>
          </form>
        </>
      )}
    </Formik>
  );
};

export default AddEditSection;
